import { EventEmitter, Inject, Injectable, Output, PipeTransform } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { EntityService } from '@services/entity/entity.service';
import { Observable } from 'rxjs';
import { PricingDto } from '@dto/pricingDto';
import { ChargingParkSubscriptionDto } from '@dto/chargingParkSubscriptionDto';
import { UserDto } from '@dto/userDto';
import { MonthEndResponseDto } from '@dto/mothEndeResponseDto';
import { InvoiceItemDto } from '@dto/invoiceItemDto';
import { RemovePricingRequest } from '@dto/removePricingRequest';
import { RemovePricingResponse } from '@dto/removePricingResponse';
import { DeletePricingRequest } from '@dto/deletePricingRequest';
import { DeletePricingResponse } from '@dto/deletePricingResponse';


@Injectable({
  providedIn: 'root'
})
export class PricingsService extends EntityService {

  public entityRoute = "pricings";

  constructor(http: HttpClient, @Inject('BASE_URL') baseUrl: string) {
    super(http, baseUrl);
  }

  @Output() pricingCreatedEvent = new EventEmitter<[PricingDto, PricingDto]>();
  emitCreated(createdPricing: PricingDto, replacedPricing?: PricingDto) {
    this.pricingCreatedEvent.emit([createdPricing, replacedPricing]);
  }

  @Output() pricingUpdatedEvent = new EventEmitter<PricingDto>();
  emitUpdated(updatedPricing: PricingDto) {
    this.pricingUpdatedEvent.emit(updatedPricing);
  }

  @Output() pricingUpdatePreparedEvent = new EventEmitter<boolean>();
  emitPrepared(prepared: boolean) {
    this.pricingUpdatePreparedEvent.emit(prepared);
  }

  createPricing(pricingDto: PricingDto): Observable<PricingDto> {
    return this.http.post<PricingDto>(this.baseUrl + 'api/pricings', pricingDto);
  }
  
  getPricingsOfUser(userId: string): Observable<PricingDto[]> {
    return this.http.get<PricingDto[]>(this.baseUrl + 'api/pricings/user/' + userId);
  }

  getPricingsOfCurrentUser(userId: string): Observable<PricingDto[]> {
    return this.http.get<PricingDto[]>(this.baseUrl + 'api/pricings/user/current/' + userId);
  }
  
  getPublicPricingOfCustomer(customerId: number): Observable<PricingDto> {
    return this.http.get<PricingDto>(this.baseUrl + 'api/pricings/public/customer/' + customerId);
  }

  getPricing(id: number): Observable<PricingDto> {
    return this.http.get<PricingDto>(this.baseUrl + 'api/pricings/' + id);
  }

  patchPricing(pricing: PricingDto): Observable<PricingDto> {
    return this.http.patch<PricingDto>(this.baseUrl + 'api/pricings/' + pricing.id, pricing);
  }

  patchPricingOfUser(patch: ChargingParkSubscriptionDto): Observable<ChargingParkSubscriptionDto> {
    return this.http.patch<ChargingParkSubscriptionDto>(this.baseUrl + 'api/pricings/patch/' + patch.userId, patch);
  }

  deletePricing(pricingId: string, request: DeletePricingRequest): Observable<DeletePricingResponse> {
    return this.http.put<DeletePricingResponse>(this.baseUrl + 'api/pricings/delete/' + pricingId, request);
  }

  getOpenInvoiceItemsOfPricing(pricingId: string): Observable<InvoiceItemDto[]> {
    return this.http.get<InvoiceItemDto[]>(this.baseUrl + 'api/pricings/' + pricingId + '/open-invoiceitems');
  }

  getUsersOfPricing(pricingId: string): Observable<UserDto[]> {
    return this.http.get<UserDto[]>(this.baseUrl + 'api/pricings/' + pricingId + '/users');
  }

  removePricingFromUsers(request: RemovePricingRequest): Observable<RemovePricingResponse> {
    return this.http.put<RemovePricingResponse>(this.baseUrl + 'api/pricings/remove-pricing', request);
  }

  getOpenInvoiceItemsOfRecipients(pricingId: number, recipientUserIds: string[]): Observable<InvoiceItemDto[]> {
    return this.http.post<InvoiceItemDto[]>(this.baseUrl + 'api/pricings/'+ pricingId +'/open-invoiceitems/recipients', recipientUserIds);
  }

  static matches(pricing: PricingDto, term: string, pipe?: PipeTransform) {
    return pricing.priceId?.toLowerCase().includes(term.toLowerCase())
      || pricing.productId?.toLowerCase().includes(term.toLowerCase())
      || pricing.name?.toLowerCase().includes(term.toLowerCase())
      || pricing.description?.toLowerCase().includes(term.toLowerCase())
  }

  //for test
  testMonthEndForCustomer(userId: string): Observable<MonthEndResponseDto>{
    return this.http.put<MonthEndResponseDto>(this.baseUrl + 'api/statictriggers/month-end/issuer/' + userId, null);
  }

  testMonthEndForAll(): Observable<MonthEndResponseDto>{
    return this.http.put<MonthEndResponseDto>(this.baseUrl + 'api/statictriggers/month-end/', null);
  }
}
